import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Link2,
  Link2Off,
  Search,
  Filter,
  Share2,
  RefreshCw,
  CalendarDays,
  X,
  Loader2,
  Mail,
  Ticket,
  UserCheck,
  Send,
  AlertTriangle,
  Users,
  CheckCircle2,
  Clock,
  UserPlus,
  Ban,
  Wallet,
} from 'lucide-react';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import Pagination from '../../Shared/Pagination/Pagination';
import { PaginationResponse } from '../../Shared/Pagination/PaginationResponse';
import { GenerateInviteResponseDTO } from '../Types/AdminType';
import { generateInviteLink, revokeInviteLink } from '../Services/AdminService';
import {
  AllInvitationByInvitedUserResponseDTO,
  InvitationStats,
} from '../../Candidate/Types/CandidateType';
import { getInvitationsByInvitedUserFiltered } from '../../Candidate/Services/CandidateService';
import { storage } from '../../Shared/Utils/authStorage';
import { formatDateTime } from '../../Shared/Utils/HelperUtils';
import SocialShareComponent from './SocialShareComponent';
import SocialShareComponentForEdit from './SocialShareComponentForEdit';

interface Props {
  menuName?: string;
}

const PAGE_SIZE = 8;

const emptyStats: InvitationStats = {
  total: 0,
  active: 0,
  used: 0,
  expired: 0,
  revoked: 0,
  registered: 0,
  inviteBalance: 0,
  inviteTotal: 0,
};

const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]';

const normalize = (status?: string) => (status || '').toUpperCase();

/**
 * These rows come from `private_invitations`, so the statuses are the invite lifecycle
 * (ACTIVE / USED / EXPIRED / REVOKED) rather than the growth module's ACCEPTED / REJECTED.
 */
const INVITE_STATUSES = ['ACTIVE', 'USED', 'EXPIRED', 'REVOKED'] as const;

const invitationStatusBadge = (status?: string) => {
  switch (normalize(status)) {
    case 'ACTIVE':
      return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400';
    case 'USED':
      return 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400';
    case 'EXPIRED':
      return 'border-slate-600/40 bg-slate-700/20 text-slate-300';
    case 'REVOKED':
      return 'border-amber-500/30 bg-amber-500/10 text-amber-400';
    default:
      return 'border-slate-600/40 bg-slate-700/20 text-slate-300';
  }
};

const GenerateInviteLinkComponent = ({ menuName = 'Invite Links' }: Props) => {
  const [rows, setRows] = useState<AllInvitationByInvitedUserResponseDTO[]>([]);
  const [pagination, setPagination] =
    useState<PaginationResponse<AllInvitationByInvitedUserResponseDTO> | null>(null);
  const [stats, setStats] = useState<InvitationStats>(emptyStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('ALL');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(0);

  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [selected, setSelected] = useState<AllInvitationByInvitedUserResponseDTO | null>(null);

  const [inviteData, setInviteData] = useState<GenerateInviteResponseDTO | null>(null);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteStatus, setInviteStatus] = useState<'success' | 'error' | ''>('');
  const [generating, setGenerating] = useState(false);
  const [revokingCode, setRevokingCode] = useState<string | null>(null);

  const userId = storage.getUserId() ?? '';

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchInvitations = useCallback(async () => {
    if (!userId) {
      setError('No user ID found. Please log in again.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await getInvitationsByInvitedUserFiltered(
        userId,
        {
          search: debouncedSearch || undefined,
          status: status !== 'ALL' ? status : undefined,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        },
        page,
        PAGE_SIZE
      );
      if (response.success && response.data) {
        const pageData = response.data.page;
        setRows(pageData.content ?? []);
        setStats(response.data.stats ?? emptyStats);
        setPagination(pageData);
        if (pageData.totalPages > 0 && page >= pageData.totalPages) {
          setPage(pageData.totalPages - 1);
        }
      } else {
        setRows([]);
        setStats(emptyStats);
        setPagination(null);
        setError(response.message || 'Could not load invite links.');
      }
    } catch (err) {
      console.error('Failed to load invite links', err);
      setRows([]);
      setStats(emptyStats);
      setPagination(null);
      setError('An unexpected error occurred while loading invite links.');
    } finally {
      setLoading(false);
    }
  }, [userId, debouncedSearch, status, fromDate, toDate, page]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  // Reset to the first page whenever the active filters change.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, status, fromDate, toDate]);

  const resetFilters = () => {
    setSearch('');
    setStatus('ALL');
    setFromDate('');
    setToDate('');
  };

  const hasActiveFilters =
    Boolean(search) || status !== 'ALL' || Boolean(fromDate) || Boolean(toDate);

  const handleGenerate = async () => {
    setGenerating(true);
    setInviteMessage('');
    setInviteStatus('');
    try {
      const response = await generateInviteLink({ userId });
      if (response.success) {
        setInviteData(response.data);
        setInviteStatus('success');
        setInviteMessage('Invite link generated successfully.');
        fetchInvitations();
      } else {
        setInviteStatus('error');
        setInviteMessage(response.message || 'Invite limit exceeded.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const openShare = (row: AllInvitationByInvitedUserResponseDTO) => {
    setSelected(row);
    setIsShareOpen(true);
  };

  const statCards = useMemo(
    () => [
      { label: 'Total Invites', value: stats.total, icon: <Users size={18} />, accent: 'text-cyan-400 border-cyan-500/30 bg-cyan-500/10' },
      { label: 'Active', value: stats.active, icon: <Clock size={18} />, accent: 'text-amber-400 border-amber-500/30 bg-amber-500/10' },
      { label: 'Used', value: stats.used, icon: <CheckCircle2 size={18} />, accent: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' },
      { label: 'Registered', value: stats.registered, icon: <UserCheck size={18} />, accent: 'text-rose-400 border-rose-500/30 bg-rose-500/10' },
      { label: 'Expired', value: stats.expired, icon: <Link2Off size={18} />, accent: 'text-slate-400 border-slate-500/30 bg-slate-500/10' },
      { label: 'Revoked', value: stats.revoked, icon: <Ban size={18} />, accent: 'text-amber-400 border-amber-500/30 bg-amber-500/10' },
    ],
    [stats]
  );

  // A link can be called back for as long as nobody spent it. An expired one already burned a
  // credit and can never register anyone, so revoking it is how the credit comes back.
  const canRevoke = (row: AllInvitationByInvitedUserResponseDTO) => {
    const current = normalize(row.invitationStatus);
    return current === 'ACTIVE' || current === 'EXPIRED';
  };

  const handleRevoke = async (row: AllInvitationByInvitedUserResponseDTO) => {
    if (!row.inviteCode) return;
    setRevokingCode(row.inviteCode);
    try {
      const response = await revokeInviteLink(row.inviteCode);
      if (response.success) {
        setAlert({
          variant: 'success',
          title: 'Invite link revoked',
          message: `${row.inviteCode} no longer works. ${
            response.data?.creditsRestored
              ? '1 invite credit was returned to the balance.'
              : ''
          }`.trim(),
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
        fetchInvitations();
      } else {
        setAlert({
          variant: 'error',
          title: 'Could not revoke link',
          message: response.message || 'This link can no longer be revoked.',
          onClose: () => setAlert(null),
        });
      }
    } catch (err) {
      console.error('Revoke invite failed', err);
      setAlert({
        variant: 'error',
        title: 'Could not revoke link',
        message: 'Something went wrong. Please try again.',
        onClose: () => setAlert(null),
      });
    } finally {
      setRevokingCode(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <Link2 size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">
              {menuName}
            </h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Generate invite links and track who has joined
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchInvitations()}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            onClick={() => {
              setInviteData(null);
              setInviteMessage('');
              setInviteStatus('');
              setIsGenerateOpen(true);
            }}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-black text-slate-950 transition hover:bg-cyan-400"
          >
            <UserPlus size={16} />
            Generate Invite
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        {statCards.map((card) => (
          <div key={card.label} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
            <div className="flex items-center justify-between">
              <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${card.accent}`}>
                {card.icon}
              </span>
              <span className="text-2xl font-black text-slate-100">{card.value}</span>
            </div>
            <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-500">
              {card.label}
            </p>
          </div>
        ))}
      </div>

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-cyan-500/30 bg-cyan-500/5 px-5 py-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <Wallet size={18} />
          </span>
          <div>
            <p className="text-[11px] font-black uppercase tracking-widest text-slate-400">
              Invite Balance
            </p>
            <p className="text-xs font-semibold text-slate-500">
              {stats.total} link{stats.total === 1 ? '' : 's'} generated in total. Revoking any
              unused link returns its credit, expired links included.
            </p>
          </div>
        </div>
        <div className="flex items-baseline gap-2">
          <span
            className={`text-3xl font-black ${
              stats.inviteBalance === 0 ? 'text-rose-400' : 'text-cyan-300'
            }`}
          >
            {stats.inviteBalance}
          </span>
          <span className="text-sm font-bold text-slate-500">
            {stats.inviteTotal > 0 ? `of ${stats.inviteTotal} granted` : 'left'}
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email or code..."
              className={`${fieldClass} pl-9`}
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-500" />
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className={fieldClass}
              >
                <option value="ALL">All Statuses</option>
                {INVITE_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
          </div>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className={fieldClass}
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className={fieldClass}
          />
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-2.5 text-sm font-bold text-slate-300 transition hover:text-slate-100"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {alert && (
        <div className="mb-6">
          <Alert {...alert} onClose={() => setAlert(null)} />
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm font-semibold text-rose-400">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
        <table className="w-full min-w-[1050px] text-left">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/60">
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Name</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Email</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Invite Code</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">User Status</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Invitation Status</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Invite Used</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Balance</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Sent At</th>
              <th className="px-5 py-4 text-right text-[10px] font-black uppercase tracking-widest text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/70">
            {loading && (
              <tr>
                <td colSpan={9} className="px-5 py-16 text-center text-sm font-semibold text-slate-500">
                  <Loader2 size={20} className="mx-auto mb-2 animate-spin" />
                  Loading invite links...
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((row, idx) => (
                <motion.tr
                  key={row.invitationId || row.inviteId || idx}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  className="transition hover:bg-slate-900/70"
                >
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-700 bg-slate-900 text-sm font-bold text-cyan-400">
                        {row.firstName?.[0] || row.email?.[0]?.toUpperCase() || '?'}
                      </div>
                      <span className="text-sm font-bold text-slate-100">
                        {`${row.firstName ?? ''} ${row.lastName ?? ''}`.trim() || 'Not registered yet'}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    {row.email ? (
                      <span title={row.email} className="text-sm font-semibold text-cyan-400">
                        {row.email.length > 24 ? `${row.email.slice(0, 24)}...` : row.email}
                      </span>
                    ) : (
                      <span className="text-slate-600">?</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1.5 font-mono text-sm font-bold text-cyan-400">
                      <Ticket size={14} />
                      {row.inviteCode || '?'}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-sm font-semibold text-slate-300">
                      {row.userStatus || 'INVITE_SENT'}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-flex rounded-lg border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${invitationStatusBadge(
                        row.invitationStatus
                      )}`}
                    >
                      {row.invitationStatus || 'UNKNOWN'}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`text-sm font-bold ${
                        row.used ? 'text-emerald-400' : 'text-slate-400'
                      }`}
                    >
                      {row.used ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-sm font-black ${
                        stats.inviteBalance === 0
                          ? 'border-rose-500/30 bg-rose-500/10 text-rose-400'
                          : 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300'
                      }`}
                    >
                      <Wallet size={12} />
                      {stats.inviteBalance}
                      {stats.inviteTotal > 0 && (
                        <span className="text-slate-500">/{stats.inviteTotal}</span>
                      )}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className="flex items-center gap-2 text-sm font-semibold text-slate-400">
                      <CalendarDays size={14} className="text-slate-600" />
                      {row.createdAt ? formatDateTime(row.createdAt) : 'N/A'}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-2">
                      {canRevoke(row) && (
                        <button
                          onClick={() => handleRevoke(row)}
                          disabled={revokingCode === row.inviteCode}
                          title="Revoke invite and return the credit"
                          className="inline-flex items-center gap-1.5 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] font-black uppercase tracking-widest text-amber-400 transition hover:bg-amber-500/20 disabled:opacity-50"
                        >
                          {revokingCode === row.inviteCode ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Ban size={14} />
                          )}
                          Revoke
                        </button>
                      )}
                      <button
                        onClick={() => openShare(row)}
                        title="Share invite"
                        className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-2 text-cyan-400 transition hover:bg-cyan-500/20"
                      >
                        <Share2 size={16} />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-5 py-16 text-center text-sm font-semibold text-slate-500">
                  No invite links match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pagination && pagination.totalElements > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2">
          <Pagination
            page={pagination.page}
            size={pagination.size}
            totalElements={pagination.totalElements}
            totalPages={pagination.totalPages}
            first={pagination.first}
            last={pagination.last}
            hasNext={pagination.hasNext}
            hasPrevious={pagination.hasPrevious}
            setPage={setPage}
          />
        </div>
      )}

      {/* Generate invite modal */}
      <AnimatePresence>
        {isGenerateOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => setIsGenerateOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
            >
              <div className="mb-6 flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-black text-slate-100">Generate Invite Link</h2>
                  <p className="text-xs font-semibold text-slate-400">
                    Create a shareable invite link for new users
                  </p>
                </div>
                <button
                  onClick={() => setIsGenerateOpen(false)}
                  className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
                >
                  <X size={18} />
                </button>
              </div>

              <button
                onClick={handleGenerate}
                disabled={generating}
                className="mb-5 flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-500 py-3 text-sm font-black text-slate-950 transition hover:bg-cyan-400 disabled:opacity-60"
              >
                {generating ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Generate Invite Link
              </button>

              {inviteStatus === 'error' && (
                <div className="mb-4 flex items-center gap-2 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm font-semibold text-rose-400">
                  <AlertTriangle size={16} />
                  {inviteMessage}
                </div>
              )}

              <SocialShareComponent
                inviteData={
                  inviteData || { inviteCode: '', inviteUrl: '', expiryAt: '' }
                }
                message={inviteMessage}
                status={inviteStatus}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share invite modal */}
      <AnimatePresence>
        {isShareOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => setIsShareOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
            >
              <div className="mb-6 flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-black text-slate-100">Share Invite</h2>
                  <p className="text-xs font-semibold text-slate-400">
                    {selected?.email || 'Invite link'}
                  </p>
                </div>
                <button
                  onClick={() => setIsShareOpen(false)}
                  className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-sm font-semibold text-slate-300">
                <Mail size={16} className="text-slate-500" />
                {selected?.inviteCode || 'No code'}
              </div>

              <SocialShareComponentForEdit
                inviteData={{
                  inviteUrl: selected?.inviteUrl || '',
                  inviteCode: selected?.inviteCode || '',
                  expiryAt: selected?.expiryAt || undefined,
                }}
                message={inviteMessage}
                status={inviteStatus}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GenerateInviteLinkComponent;
